// ═══════════════════════════════════════════════════════════
// V·IANet Requests — Detalle de Solicitud
// Formulario readOnly + timeline aprobaciones + decidir
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { request } from '../../shared/services/api';
import { useI18n } from '../../../i18n';
import { FormSchemaRenderer } from './components/FormSchemaRenderer';
import type { FormComponent } from './components/FormSchemaRenderer';

interface SolicitudDetalle {
  id: string;
  codigo: string;
  estado: string;
  valores: Record<string, unknown>;
  tipoSolicitud: {
    codigo: string;
    nombre: string;
    requiereFirma?: boolean;
    nivelFirma?: string | null;
  };
  formularioSchema: { schema: { components: FormComponent[] } } | null;
  solicitante: { nombre: string; apellidos: string } | null;
  creadoEn: string;
  actualizadoEn: string;
  pasos?: PasoAprobacion[];
  auditLog?: AuditEntry[];
}

interface PasoAprobacion {
  id: string;
  nombre: string;
  orden: number;
  estado: string;
  aprobador?: { nombre: string; apellidos: string } | null;
  decidido: boolean;
  decision?: string | null;
  observaciones?: string | null;
  fechaDecision?: string | null;
}

interface AuditEntry {
  id: string;
  accion: string;
  actor?: { nombre: string; apellidos: string } | null;
  creadoEn: string;
  datosAntes?: unknown;
  datosDespues?: unknown;
}

const ESTADO_COLORS: Record<string, { bg: string; text: string }> = {
  BORRADOR: { bg: '#F3F4F6', text: '#6B7280' },
  ENVIADA: { bg: '#DBEAFE', text: '#1E40AF' },
  EN_APROBACION: { bg: '#FEF3C7', text: '#92400E' },
  APROBADA: { bg: '#D1FAE5', text: '#065F46' },
  RECHAZADA: { bg: '#FEE2E2', text: '#991B1B' },
  CANCELADA: { bg: '#F3F4F6', text: '#9CA3AF' },
  DEVUELTA: { bg: '#FDE68A', text: '#78350F' },
  PENDIENTE: { bg: '#FEF3C7', text: '#92400E' },
};

function Badge({ estado }: { estado: string }) {
  const c = ESTADO_COLORS[estado] ?? ESTADO_COLORS.BORRADOR;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 12px',
        borderRadius: 12,
        background: c.bg,
        color: c.text,
      }}
    >
      {estado.replace(/_/g, ' ')}
    </span>
  );
}

export function RequestDetailPage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sol, setSol] = useState<SolicitudDetalle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDecisionModal, setShowDecisionModal] = useState<{
    pasoId: string;
    decision: string;
  } | null>(null);
  const [observaciones, setObservaciones] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    request<{ data: SolicitudDetalle }>(`/requests/solicitudes/${id}`)
      .then((res) => setSol(res.data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleDecision = async () => {
    if (!showDecisionModal) return;
    setDeciding(true);
    try {
      await request<unknown>(`/requests/pasos/${showDecisionModal.pasoId}/decidir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: showDecisionModal.decision, observaciones }),
      });
      setToast(`${showDecisionModal.decision} registrada`);
      setShowDecisionModal(null);
      setObservaciones('');
      // Reload
      const res = await request<{ data: SolicitudDetalle }>(`/requests/solicitudes/${id}`);
      setSol(res.data);
    } catch {
      setToast(t.requests.errorGenerico);
    } finally {
      setDeciding(false);
    }
  };

  if (loading)
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontFamily: 'DM Sans' }}>
        {t.common.cargando}
      </div>
    );
  if (error || !sol)
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#EF4444', fontFamily: 'DM Sans' }}>
        {error ?? 'No encontrada'}
      </div>
    );

  const schema = sol.formularioSchema?.schema;

  return (
    <div
      style={{ padding: 32, maxWidth: 900, margin: '0 auto', fontFamily: 'DM Sans, sans-serif' }}
    >
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            padding: '10px 20px',
            borderRadius: 8,
            background: '#1F3864',
            color: 'white',
            fontSize: 13,
            fontWeight: 600,
            zIndex: 999,
          }}
        >
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate('/requests/mis')}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: '#2F5496',
            fontSize: 13,
            fontWeight: 600,
            padding: 0,
            marginBottom: 12,
          }}
        >
          &larr; {t.requests.misSolicitudes}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: 14,
              fontWeight: 700,
              color: '#2F5496',
              background: '#2F549610',
              padding: '4px 12px',
              borderRadius: 6,
            }}
          >
            {sol.codigo ?? sol.id.slice(0, 8)}
          </span>
          <Badge estado={sol.estado} />
          {sol.estado === 'APROBADA' && sol.tipoSolicitud?.requiereFirma && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 10px',
                borderRadius: 12,
                background: '#D1FAE5',
                color: '#065F46',
              }}
              title={
                sol.tipoSolicitud.nivelFirma ? `Nivel: ${sol.tipoSolicitud.nivelFirma}` : undefined
              }
            >
              <svg
                width={12}
                height={12}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
              {t.requests.firmada ?? 'Firmada'}
            </span>
          )}
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1F3864', margin: '0 0 4px 0' }}>
          {sol.tipoSolicitud?.nombre ?? 'Solicitud'}
        </h1>
        <div style={{ fontSize: 12, color: '#6B7280' }}>
          {sol.solicitante ? `${sol.solicitante.nombre} ${sol.solicitante.apellidos}` : '—'}{' '}
          &middot; {new Date(sol.creadoEn).toLocaleDateString('es-ES')}
        </div>
      </div>

      {/* Formulario readOnly */}
      {schema && (
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            border: '1px solid #E5E7EB',
            padding: 24,
            marginBottom: 24,
          }}
        >
          <h2
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: '#1F3864',
              marginTop: 0,
              marginBottom: 16,
            }}
          >
            {t.requests.formulario}
          </h2>
          <FormSchemaRenderer
            schema={schema}
            values={sol.valores ?? {}}
            onChange={() => {}}
            readOnly
          />
        </div>
      )}

      {/* Timeline aprobaciones */}
      {sol.pasos && sol.pasos.length > 0 && (
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            border: '1px solid #E5E7EB',
            padding: 24,
            marginBottom: 24,
          }}
        >
          <h2
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: '#1F3864',
              marginTop: 0,
              marginBottom: 16,
            }}
          >
            {t.requests.timeline}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {sol.pasos.map((paso, i) => (
              <div
                key={paso.id}
                style={{
                  display: 'flex',
                  gap: 12,
                  position: 'relative',
                  paddingBottom: i < (sol.pasos ?? []).length - 1 ? 16 : 0,
                }}
              >
                {/* Vertical line */}
                {i < (sol.pasos ?? []).length - 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 11,
                      top: 24,
                      bottom: 0,
                      width: 2,
                      background: '#E5E7EB',
                    }}
                  />
                )}
                {/* Dot */}
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    flexShrink: 0,
                    background: paso.decidido
                      ? paso.decision === 'APROBADO'
                        ? '#D1FAE5'
                        : paso.decision === 'RECHAZADO'
                          ? '#FEE2E2'
                          : '#FEF3C7'
                      : '#F3F4F6',
                    border: `2px solid ${paso.decidido ? (paso.decision === 'APROBADO' ? '#065F46' : paso.decision === 'RECHAZADO' ? '#991B1B' : '#92400E') : '#D1D5DB'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 700,
                    color: paso.decidido ? '#374151' : '#9CA3AF',
                  }}
                >
                  {paso.orden}
                </div>
                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
                      {paso.nombre}
                    </span>
                    <Badge estado={paso.decidido ? (paso.decision ?? 'PENDIENTE') : 'PENDIENTE'} />
                  </div>
                  {paso.aprobador && (
                    <div style={{ fontSize: 12, color: '#6B7280' }}>
                      {paso.aprobador.nombre} {paso.aprobador.apellidos}
                    </div>
                  )}
                  {paso.observaciones && (
                    <div
                      style={{ fontSize: 12, color: '#374151', marginTop: 4, fontStyle: 'italic' }}
                    >
                      "{paso.observaciones}"
                    </div>
                  )}
                  {paso.fechaDecision && (
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                      {new Date(paso.fechaDecision).toLocaleString('es-ES')}
                    </div>
                  )}
                  {/* Decision buttons if pending and I'm approver */}
                  {!paso.decidido && sol.estado === 'EN_APROBACION' && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button
                        onClick={() =>
                          setShowDecisionModal({ pasoId: paso.id, decision: 'APROBADO' })
                        }
                        style={{
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 16px',
                          background: '#065F46',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: 'inherit',
                        }}
                      >
                        {t.requests.aprobar}
                      </button>
                      <button
                        onClick={() =>
                          setShowDecisionModal({ pasoId: paso.id, decision: 'RECHAZADO' })
                        }
                        style={{
                          border: 'none',
                          borderRadius: 6,
                          padding: '6px 16px',
                          background: '#991B1B',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: 'inherit',
                        }}
                      >
                        {t.requests.rechazar}
                      </button>
                      <button
                        onClick={() =>
                          setShowDecisionModal({ pasoId: paso.id, decision: 'DEVUELTO' })
                        }
                        style={{
                          border: '1px solid #E5E7EB',
                          borderRadius: 6,
                          padding: '6px 16px',
                          background: 'white',
                          color: '#374151',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: 'inherit',
                        }}
                      >
                        {t.requests.devolver}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit log (collapsible) */}
      {sol.auditLog && sol.auditLog.length > 0 && (
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
        >
          <button
            onClick={() => setShowAudit(!showAudit)}
            style={{
              width: '100%',
              padding: '14px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontFamily: 'inherit',
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1F3864' }}>
              {t.requests.auditLog} ({sol.auditLog.length})
            </span>
            <span style={{ color: '#9CA3AF', fontSize: 12 }}>{showAudit ? '▲' : '▼'}</span>
          </button>
          {showAudit && (
            <div
              style={{ padding: '0 24px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              {sol.auditLog.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    fontSize: 12,
                    color: '#6B7280',
                    padding: '4px 0',
                    borderTop: '1px solid #F3F4F6',
                  }}
                >
                  <span style={{ minWidth: 140, color: '#9CA3AF' }}>
                    {new Date(entry.creadoEn).toLocaleString('es-ES')}
                  </span>
                  <span style={{ fontWeight: 600, color: '#374151' }}>{entry.accion}</span>
                  {entry.actor && (
                    <span>
                      {entry.actor.nombre} {entry.actor.apellidos}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Decision modal */}
      {showDecisionModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 24,
              width: 420,
              maxWidth: '90vw',
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            <h3
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: '#1F3864',
                marginTop: 0,
                marginBottom: 16,
              }}
            >
              {showDecisionModal.decision === 'APROBADO'
                ? t.requests.aprobar
                : showDecisionModal.decision === 'RECHAZADO'
                  ? t.requests.rechazar
                  : t.requests.devolver}
            </h3>
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder={t.requests.observaciones}
              rows={4}
              style={{
                width: '100%',
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13,
                fontFamily: 'inherit',
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={() => {
                  setShowDecisionModal(null);
                  setObservaciones('');
                }}
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 8,
                  padding: '8px 16px',
                  background: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'inherit',
                }}
              >
                {t.common.cancelar}
              </button>
              <button
                onClick={handleDecision}
                disabled={deciding}
                style={{
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  background:
                    showDecisionModal.decision === 'APROBADO'
                      ? '#065F46'
                      : showDecisionModal.decision === 'RECHAZADO'
                        ? '#991B1B'
                        : '#1F3864',
                  color: 'white',
                  cursor: deciding ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                {t.requests.confirmar}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
